import { useRef } from "react";
import { motion, useTransform, useScroll } from "framer-motion";
import { ArrowDown, Play } from "lucide-react";
import { heroLines, profile } from "@/data/content";
import { EASE, scrollToId } from "./Reveal";

import { MotionLines } from "./MotionLines";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const linesY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section id="top" ref={ref}
      className="hero-grid relative overflow-hidden bg-white pb-20 pt-32 sm:pt-40 lg:pb-28" data-testid="hero-section">
      <MotionLines id="hero-motion-path" style={{ y: linesY }} className="opacity-60 sm:opacity-100" testId="hero-line-drawing" />
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-11">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#E0E8F2] bg-[#F0F5FA] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#09549F]"
            data-testid="hero-availability">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#0085B3] opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#005EB8]" /></span>
            {profile.availability}
          </motion.p>
          <h1 className="text-[2.6rem] font-bold leading-[1.02] tracking-[-0.04em] text-[#05325F] sm:text-6xl lg:text-[5rem] xl:text-[5.75rem]" data-testid="hero-headline">
            {heroLines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-[0.06em]">
                <motion.span className={`block ${i === 1 ? "text-[#005EB8]" : ""}`}
                  initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.2, delay: 0.25 + i * 0.14, ease: EASE }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#4A607A] md:text-lg">
            I'm {profile.name}, a {profile.role.toLowerCase()} with a background in 3D modelling and animation, motion graphics, video editing and compositing, course creation and delivery.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollToId("reel")} data-testid="hero-watch-reel-button"
              className="group flex items-center gap-3 rounded-full bg-[#05325F] py-2 pl-2 pr-6 font-bold text-white transition-colors hover:bg-[#005EB8]">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#05325F] transition-transform duration-500 group-hover:rotate-[120deg]">
                <Play className="h-4 w-4 fill-current" />
              </span>
              Watch the reel
            </button>
            <button onClick={() => scrollToId("animations")} data-testid="hero-view-work-button"
              className="group flex items-center gap-2 rounded-full border-2 border-[#05325F] px-6 py-3.5 font-bold text-[#05325F] transition-colors hover:bg-[#F0F5FA]">
              My animations <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-1" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
